import { useEffect, useRef, useState } from 'react';

/**
 * Faixa que rola de lado no celular (abas, secoes da ficha). Diz se ha mais
 * conteudo escondido em cada borda e traz o item ativo para a vista quando
 * `ativo` muda. So a faixa rola: scrollIntoView rolaria a pagina junto.
 */
export function useFaixaRolavel<T extends HTMLElement>(ativo: unknown, seletorAtivo: string) {
  const ref = useRef<T>(null);
  const [resta, setResta] = useState({ esquerda: false, direita: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () =>
      setResta({ esquerda: el.scrollLeft > 4, direita: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    el.addEventListener('scroll', medir, { passive: true });
    return () => {
      obs.disconnect();
      el.removeEventListener('scroll', medir);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    const item = el?.querySelector<HTMLElement>(seletorAtivo);
    if (!el || !item) return;
    const folga = 24;
    const inicio = item.offsetLeft - el.offsetLeft;
    if (inicio - folga < el.scrollLeft) el.scrollLeft = inicio - folga;
    else if (inicio + item.offsetWidth + folga > el.scrollLeft + el.clientWidth)
      el.scrollLeft = inicio + item.offsetWidth + folga - el.clientWidth;
  }, [ativo, seletorAtivo]);

  return { ref, resta };
}

/** Classes da faixa: rola de lado sem barra visivel e sem puxar o "voltar" do navegador. */
export const classeFaixaRolavel = 'flex overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/** Degrade nas bordas com conteudo escondido. O pai precisa ser `relative`. */
export function SombrasDaFaixa({ resta, cor = 'from-fundo' }: { resta: { esquerda: boolean; direita: boolean }; cor?: string }) {
  return (
    <>
      {resta.esquerda && (
        <span aria-hidden className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r ${cor} to-transparent`} />
      )}
      {resta.direita && (
        <span aria-hidden className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-linear-to-l ${cor} to-transparent`} />
      )}
    </>
  );
}
